import * as pc from "playcanvas";
import { buildMolotov } from "./molotovDesign";

/**
 * Procedural grenade model — the exact geometry used for the first-person
 * in-hand grenade, extracted so it can be reused for the in-flight grenade
 * (PeerGrenadeRenderer) too. The grenade you throw is then literally the
 * same model you see in your hand, not a plain sphere.
 *
 * `weaponId`: "smoke" | "flash" | "molotov" | "incendiary" | anything else
 * → default HE. The in-flight wire kind ("he"/"flash"/"smoke"/"molotov")
 * maps straight through.
 */
function mat(r: number, g: number, b: number, gloss = 0.35): pc.StandardMaterial {
  const m = new pc.StandardMaterial();
  m.diffuse = new pc.Color(r, g, b);
  m.gloss = gloss;
  m.update();
  return m;
}

function addPart(
  parent: pc.Entity,
  type: "box" | "cylinder" | "capsule" | "sphere",
  name: string,
  scale: [number, number, number],
  pos: [number, number, number],
  euler: [number, number, number],
  material: pc.StandardMaterial,
): void {
  const e = new pc.Entity(name);
  e.addComponent("render", { type });
  e.setLocalScale(scale[0], scale[1], scale[2]);
  e.setLocalPosition(pos[0], pos[1], pos[2]);
  e.setLocalEulerAngles(euler[0], euler[1], euler[2]);
  const render = e.render;
  if (render) {
    render.material = material;
    render.castShadows = false;
    render.receiveShadows = false;
  }
  parent.addChild(e);
}

export function buildGrenadeModel(parent: pc.Entity, weaponId: string): void {
  const steel = mat(0.38, 0.39, 0.42, 0.55);
  const dark = mat(0.12, 0.12, 0.13, 0.25);

  if (weaponId === "smoke") {
    // Smoke Grenade (Cylindrical, silver/grey with green stripe)
    const smokeBody = mat(0.6, 0.6, 0.6, 0.4);
    const smokeStripe = mat(0.2, 0.8, 0.2, 0.2);
    const smokeTop = mat(0.3, 0.3, 0.3, 0.5);

    addPart(parent, "cylinder", "smoke-body", [0.06, 0.12, 0.06], [0, -0.02, 0], [0, 0, 0], smokeBody);
    addPart(parent, "cylinder", "smoke-stripe", [0.062, 0.02, 0.062], [0, 0.02, 0], [0, 0, 0], smokeStripe);
    addPart(parent, "cylinder", "smoke-cap", [0.06, 0.01, 0.06], [0, 0.045, 0], [0, 0, 0], smokeTop);

    // Top mechanism
    addPart(parent, "cylinder", "smoke-neck", [0.02, 0.02, 0.02], [0, 0.06, 0], [0, 0, 0], steel);
    addPart(parent, "box", "smoke-spoon", [0.015, 0.08, 0.015], [0.02, 0.04, 0], [0, 0, 10], steel);
    addPart(parent, "cylinder", "smoke-pin-ring", [0.02, 0.005, 0.02], [-0.02, 0.07, 0], [90, 0, 0], steel);

    // Vent holes (fake with dark boxes)
    addPart(parent, "box", "smoke-vent1", [0.01, 0.01, 0.065], [0, 0.04, 0], [0, 45, 0], dark);
    addPart(parent, "box", "smoke-vent2", [0.01, 0.01, 0.065], [0, 0.04, 0], [0, -45, 0], dark);

  } else if (weaponId === "flash") {
    // Flashbang (Cylindrical, metallic, blue stripe)
    const flashBody = mat(0.7, 0.7, 0.75, 0.8); // Shiny metal
    const flashStripe = mat(0.2, 0.4, 0.9, 0.3);

    addPart(parent, "cylinder", "flash-body", [0.05, 0.11, 0.05], [0, -0.02, 0], [0, 0, 0], flashBody);
    addPart(parent, "cylinder", "flash-stripe", [0.052, 0.015, 0.052], [0, 0.01, 0], [0, 0, 0], flashStripe);

    // Top mechanism
    addPart(parent, "cylinder", "flash-neck", [0.02, 0.015, 0.02], [0, 0.04, 0], [0, 0, 0], steel);
    addPart(parent, "box", "flash-spoon", [0.012, 0.07, 0.012], [0.015, 0.02, 0], [0, 0, 10], steel);
    addPart(parent, "cylinder", "flash-pin-ring", [0.015, 0.005, 0.015], [-0.015, 0.05, 0], [90, 0, 0], steel);

    // Indentations (fake with dark cylinders)
    addPart(parent, "cylinder", "flash-indent1", [0.051, 0.005, 0.051], [0, -0.05, 0], [0, 0, 0], dark);
    addPart(parent, "cylinder", "flash-indent2", [0.051, 0.005, 0.051], [0, -0.01, 0], [0, 0, 0], dark);

  } else if (weaponId === "molotov") {
    // Molotov — CS:GO-style translucent green bottle with visible fuel
    // and a lit rag wick. Geometry is shared with the in-hand view-model
    // (weaponBuilder) via player/molotovDesign.ts so the thrown bottle
    // matches the held one.
    buildMolotov(parent, addPart, mat);

  } else if (weaponId === "incendiary") {
    // Incendiary — CT tactical can: squat cylinder, blue-grey shell, red hazard bands
    const canShell = mat(0.38, 0.44, 0.48, 0.6);
    const canPaint = mat(0.52, 0.58, 0.62, 0.55);
    const canDark = mat(0.14, 0.16, 0.18, 0.4);
    const hazardRed = mat(0.82, 0.12, 0.06, 0.35);
    const hazardOrange = mat(0.92, 0.42, 0.06, 0.3);
    const stencil = mat(0.9, 0.92, 0.88, 0.28);
    const nozzle = mat(0.22, 0.24, 0.26, 0.5);

    // Wider, shorter profile than molotov bottle — reads as metal canister
    addPart(parent, "cylinder", "incendiary-body", [0.058, 0.11, 0.058], [0, -0.028, 0], [0, 0, 0], canShell);
    addPart(parent, "cylinder", "incendiary-body-rim-top", [0.06, 0.008, 0.06], [0, 0.028, 0], [0, 0, 0], canDark);
    addPart(parent, "cylinder", "incendiary-body-rim-bot", [0.06, 0.008, 0.06], [0, -0.086, 0], [0, 0, 0], canDark);
    addPart(parent, "cylinder", "incendiary-paint-panel", [0.056, 0.075, 0.056], [0, -0.02, 0], [0, 0, 0], canPaint);

    // Hazard stripes (upper + lower) — CS-style red bands
    addPart(parent, "cylinder", "incendiary-band-upper", [0.059, 0.012, 0.059], [0, 0.012, 0], [0, 0, 0], hazardRed);
    addPart(parent, "cylinder", "incendiary-band-mid", [0.059, 0.01, 0.059], [0, -0.045, 0], [0, 0, 0], hazardOrange);
    addPart(parent, "cylinder", "incendiary-band-lower", [0.059, 0.012, 0.059], [0, -0.078, 0], [0, 0, 0], hazardRed);

    // Front flame pictogram + "INC" block (stencil look)
    addPart(parent, "box", "incendiary-label-plate", [0.048, 0.055, 0.004], [0, -0.022, -0.058], [0, 0, 0], stencil);
    addPart(parent, "box", "incendiary-flame-stem", [0.01, 0.028, 0.004], [0, -0.024, -0.061], [0, 0, 0], hazardOrange);
    addPart(parent, "sphere", "incendiary-flame-head", [0.016, 0.018, 0.004], [0.005, -0.008, -0.062], [0, 0, 0], hazardRed);
    addPart(parent, "box", "incendiary-text-i", [0.008, 0.022, 0.004], [-0.014, -0.038, -0.061], [0, 0, 0], canDark);
    addPart(parent, "box", "incendiary-text-nc", [0.02, 0.012, 0.004], [0.016, -0.042, -0.061], [0, 0, 0], canDark);

    // Side grip ribs + top pull-ring assembly (grenade fuse, not bottle cork)
    addPart(parent, "box", "incendiary-rib-l", [0.005, 0.09, 0.012], [-0.058, -0.025, 0], [0, 0, 0], canDark);
    addPart(parent, "box", "incendiary-rib-r", [0.005, 0.09, 0.012], [0.058, -0.025, 0], [0, 0, 0], canDark);
    addPart(parent, "cylinder", "incendiary-nozzle", [0.04, 0.02, 0.04], [0, 0.048, 0], [0, 0, 0], nozzle);
    addPart(parent, "cylinder", "incendiary-fuse-post", [0.018, 0.028, 0.018], [0, 0.072, 0], [0, 0, 0], steel);
    addPart(parent, "box", "incendiary-spoon", [0.012, 0.075, 0.012], [0.022, 0.048, 0], [0, 0, 14], steel);
    addPart(parent, "cylinder", "incendiary-pin-ring", [0.02, 0.005, 0.02], [-0.022, 0.086, 0], [90, 0, 0], steel);
    addPart(parent, "box", "incendiary-lever-tab", [0.032, 0.01, 0.022], [0, 0.09, -0.018], [0, 0, 0], hazardRed);

  } else {
    // Default HE Grenade (Pineapple shape, olive green)
    const heBody = mat(0.22, 0.32, 0.12, 0.2); // Olive drab

    // Main Body (approximating pineapple with a sphere and ribs)
    addPart(parent, "sphere", "he-body", [0.07, 0.09, 0.07], [0, -0.02, 0], [0, 0, 0], heBody);

    // Ribs (vertical and horizontal for pineapple texture)
    for (let i = 0; i < 4; i++) {
      addPart(parent, "box", `he-rib-v${i}`, [0.075, 0.08, 0.01], [0, -0.02, 0], [0, i * 45, 0], dark);
    }
    addPart(parent, "box", "he-rib-h1", [0.075, 0.01, 0.075], [0, -0.04, 0], [0, 0, 0], dark);
    addPart(parent, "box", "he-rib-h2", [0.075, 0.01, 0.075], [0, 0, 0], [0, 0, 0], dark);

    // Top mechanism
    addPart(parent, "cylinder", "he-neck", [0.025, 0.02, 0.025], [0, 0.035, 0], [0, 0, 0], steel);
    addPart(parent, "box", "he-spoon", [0.015, 0.08, 0.015], [0.02, 0.01, 0], [0, 0, 10], steel);
    addPart(parent, "cylinder", "he-pin-ring", [0.02, 0.005, 0.02], [-0.02, 0.045, 0], [90, 0, 0], steel);

    // Yellow stripe (HE indicator)
    addPart(parent, "cylinder", "he-stripe", [0.026, 0.005, 0.026], [0, 0.04, 0], [0, 0, 0], mat(0.8, 0.8, 0.1, 0.2));
  }
}
